/**
 * Parse a CSS color string (hex 3/6/8, rgb(), rgba()) into {r, g, b, a}.
 * Returns null for invalid, empty, 'transparent', or 'none' inputs.
 */
export function parseColor(raw) {
    if (!raw)
        return null;
    const s = raw.trim().toLowerCase();
    if (s === 'transparent' || s === 'none')
        return null;
    // hex 8-digit #rrggbbaa
    const hex8 = s.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/);
    if (hex8) {
        return {
            r: parseInt(hex8[1], 16),
            g: parseInt(hex8[2], 16),
            b: parseInt(hex8[3], 16),
            a: parseInt(hex8[4], 16) / 255,
        };
    }
    // hex 6-digit #rrggbb
    const hex6 = s.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/);
    if (hex6) {
        return {
            r: parseInt(hex6[1], 16),
            g: parseInt(hex6[2], 16),
            b: parseInt(hex6[3], 16),
            a: 1,
        };
    }
    // hex 3-digit #rgb
    const hex3 = s.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
    if (hex3) {
        return {
            r: parseInt(hex3[1] + hex3[1], 16),
            g: parseInt(hex3[2] + hex3[2], 16),
            b: parseInt(hex3[3] + hex3[3], 16),
            a: 1,
        };
    }
    // rgb(r, g, b)
    const rgb = s.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
    if (rgb) {
        return {
            r: parseInt(rgb[1], 10),
            g: parseInt(rgb[2], 10),
            b: parseInt(rgb[3], 10),
            a: 1,
        };
    }
    // rgba(r, g, b, a)
    const rgba = s.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/);
    if (rgba) {
        return {
            r: parseInt(rgba[1], 10),
            g: parseInt(rgba[2], 10),
            b: parseInt(rgba[3], 10),
            a: parseFloat(rgba[4]),
        };
    }
    return null;
}
/** Convert a single sRGB channel [0,255] to linear light. */
function srgbToLinear(c) {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
/** Convert linear-light RGB to CIE XYZ (D65 illuminant). */
function linearRgbToXyz(r, g, b) {
    const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
    const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
    const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041;
    return [x, y, z];
}
/** Convert CIE XYZ to CIE Lab (D65 reference white). */
function xyzToLab(x, y, z) {
    // D65 reference white
    const xn = 0.95047;
    const yn = 1.00000;
    const zn = 1.08883;
    const fx = f(x / xn);
    const fy = f(y / yn);
    const fz = f(z / zn);
    const L = 116 * fy - 16;
    const a = 500 * (fx - fy);
    const b = 200 * (fy - fz);
    return [L, a, b];
}
function f(t) {
    const delta = 6 / 29;
    return t > delta * delta * delta
        ? Math.cbrt(t)
        : t / (3 * delta * delta) + 4 / 29;
}
/** Convert a ParsedColor to CIE Lab [L*, a*, b*]. */
function parsedColorToLab(c) {
    const rl = srgbToLinear(c.r);
    const gl = srgbToLinear(c.g);
    const bl = srgbToLinear(c.b);
    const [x, y, z] = linearRgbToXyz(rl, gl, bl);
    return xyzToLab(x, y, z);
}
/**
 * Calculate CIE76 ΔE between two CSS color strings.
 * Returns null for unparseable or translucent colors. Perceived translucent
 * color depends on its backdrop; comparing RGB alone would invent a match.
 */
export function colorDeltaE(color1, color2) {
    const c1 = parseColor(color1);
    const c2 = parseColor(color2);
    if (!c1 || !c2 || c1.a !== 1 || c2.a !== 1)
        return null;
    const [L1, a1, b1] = parsedColorToLab(c1);
    const [L2, a2, b2] = parsedColorToLab(c2);
    return Math.sqrt(Math.pow(L1 - L2, 2) +
        Math.pow(a1 - a2, 2) +
        Math.pow(b1 - b2, 2));
}
/**
 * Normalize opaque colors to lowercase hex and retain alpha in rgba().
 * Returns null for invalid input.
 */
export function normalizeColor(raw) {
    const c = parseColor(raw);
    if (!c)
        return null;
    if (c.a !== 1)
        return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
    const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
    return `#${toHex(c.r)}${toHex(c.g)}${toHex(c.b)}`;
}
//# sourceMappingURL=colorUtils.js.map